import { apiClient, type ApiResponse } from '@api/apiClient';
import {
  CollapsibleCard,
  Grid,
  Stack,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { BasicTable, Button, ToastType } from '@components';
import { Column } from '@components/BasicTable/BasicTable';
import { DatePicker } from '@components/DateTimePickers/DatePicker';
import { TimePicker } from '@components/DateTimePickers/TimePicker';
import { IntegerTimeInput } from '@components/IntegerTimeInput';
import { PopoverCell } from '@components/Grid/components';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { setLoadBoardStore } from '@store/loadboard/store';
import { UNSAVED_CHANGES_KEYS, setUnsavedChanges } from '@store/unsavedChanges';
import {
  EdiUpdate,
  LoadEdiStatusUpdatePayload,
  orderStore,
  sendEDIUpdate,
  setOrderStore,
  stops,
} from '@store/orders';
import {
  ArrowDownCircleOutlineIcon,
  ArrowUpCircleOutlineIcon,
  ContentSaveIcon,
} from '@atgfr8/icons';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import {
  timeIntegerToTimeString,
  timeStringToTimeInteger,
} from '@utils/dateFormat';
import { handleToast } from '@utils/utils';
import { handleError } from '@views/order/helpers';
import { DateTime } from 'luxon';
import { createEffect, createSignal, onCleanup, Show } from 'solid-js';
import { produce } from 'solid-js/store';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';

import {
  canEditDriverInTimeFor,
  canEditDriverOutTimeFor,
  collectStopChronologyViolations,
  createShowAuto214,
  dirtyTracking,
  validateStopTimeChangeAtPositionFor,
} from './StopHelpers';

const iconStyles = {
  color: '#0070A2',
  cursor: 'pointer',
};

type Props = {
  changeTrackingSource?: ChangeTrackingSource;
  tabIndex: number;
  loading: boolean;
  readonly?: boolean;
};

export const StopsLbSidebar = (props: Props) => {
  const isLtlMode = () =>
    orderStore.order.loads[props.tabIndex]?.mode === 'LTL';
  const [isDirty, setIsDirty] = createSignal(false);

  createEffect(() => {
    setUnsavedChanges(UNSAVED_CHANGES_KEYS.loadboardStops, isDirty());
  });

  onCleanup(() => {
    setUnsavedChanges(UNSAVED_CHANGES_KEYS.loadboardStops, false);
  });

  // The loadboard path can render this sidebar with a tabIndex that differs
  // from `orderStore.activeTab.index`. Always validate / gate against the
  // load this instance is rendering, not the order store's active tab.
  const sidebarLoad = () => orderStore.order.loads[props.tabIndex];
  const sidebarStops = (): LastUsedTopStopModel[] => sidebarLoad().stops ?? [];
  const sidebarMode = () => sidebarLoad().mode;

  const sidebarCarrierMissing = (): boolean =>
    isFeatureFlagEnabled(
      FeatureFlags.RequireCarrierAssignmentToProgressStatus,
    )() && !sidebarLoad().carrierId;

  const canEditSidebarDriverIn = (index: number): boolean =>
    !sidebarCarrierMissing() &&
    canEditDriverInTimeFor(sidebarStops(), index, sidebarMode());

  const canEditSidebarDriverOut = (index: number): boolean =>
    !sidebarCarrierMissing() &&
    canEditDriverOutTimeFor(sidebarStops(), index, sidebarMode());

  type RowScratch = {
    driverInDate?: string;
    driverInTime?: string;
    driverOutDate?: string;
    driverOutTime?: string;
    driverInError?: string;
    driverOutError?: string;
  };
  const [rowScratch, setRowScratch] = createSignal<
    Partial<Record<number, RowScratch>>
  >({});

  createEffect(() => {
    props.tabIndex;
    setRowScratch({});
    setIsDirty(false);
  });

  const updateRowScratch = (index: number, patch: Partial<RowScratch>) => {
    setRowScratch((prev) => {
      const merged: RowScratch = { ...(prev[index] ?? {}), ...patch };
      (Object.keys(patch) as Array<keyof RowScratch>).forEach((k) => {
        if (patch[k] === undefined) delete merged[k];
      });
      const next = { ...prev };
      if (Object.keys(merged).length === 0) {
        delete next[index];
      } else {
        next[index] = merged;
      }
      return next;
    });
  };

  const getActiveStopIndices = (): number[] => {
    const indices: number[] = [];
    orderStore.order.loads[props.tabIndex]?.stops?.forEach((stop, index) => {
      if (stop.operationType !== 'Delete') indices.push(index);
    });
    return indices;
  };

  const revalidateAllStops = () => {
    const loadIndex = props.tabIndex;
    const stops = orderStore.order.loads[loadIndex]?.stops ?? [];
    const mode = orderStore.order.loads[loadIndex]?.mode;
    const activeIndices = getActiveStopIndices();
    const commits: Array<{
      index: number;
      field: 'driverInTime' | 'driverOutTime';
      value: string;
    }> = [];
    setRowScratch((prev) => {
      const next: Partial<Record<number, RowScratch>> = {};
      stops.forEach((stop, index) => {
        const position = activeIndices.indexOf(index);
        if (position < 0) return;
        const existing = prev[index] ?? {};
        const row: RowScratch = {
          driverInDate: existing.driverInDate,
          driverInTime: existing.driverInTime,
          driverOutDate: existing.driverOutDate,
          driverOutTime: existing.driverOutTime,
        };
        const inDate =
          existing.driverInDate ?? stop.driverInTime?.split('T')[0] ?? '';
        const inTime =
          existing.driverInTime ?? stop.driverInTime?.split('T')[1] ?? '';
        const outDate =
          existing.driverOutDate ?? stop.driverOutTime?.split('T')[0] ?? '';
        const outTime =
          existing.driverOutTime ?? stop.driverOutTime?.split('T')[1] ?? '';
        const inIso = inDate && inTime ? `${inDate}T${inTime}` : null;
        const outIso = outDate && outTime ? `${outDate}T${outTime}` : null;
        // Was the In side currently held only in scratch (not yet committed)?
        const inWasScratched =
          existing.driverInDate !== undefined ||
          existing.driverInTime !== undefined;
        const outWasScratched =
          existing.driverOutDate !== undefined ||
          existing.driverOutTime !== undefined;
        if (inIso) {
          const r = validateStopTimeChangeAtPositionFor(
            stops,
            mode,
            position,
            'driverInTime',
            inIso,
            outIso,
          );
          if (!r.ok) {
            row.driverInError = r.message;
          } else if (inWasScratched) {
            commits.push({
              index,
              field: 'driverInTime',
              value: inIso,
            });
            row.driverInDate = undefined;
            row.driverInTime = undefined;
          }
        }
        if (outIso) {
          const r = validateStopTimeChangeAtPositionFor(
            stops,
            mode,
            position,
            'driverOutTime',
            outIso,
            inIso,
          );
          if (!r.ok) {
            row.driverOutError = r.message;
          } else if (outWasScratched) {
            commits.push({
              index,
              field: 'driverOutTime',
              value: outIso,
            });
            row.driverOutDate = undefined;
            row.driverOutTime = undefined;
          }
        }
        (Object.keys(row) as Array<keyof RowScratch>).forEach((k) => {
          if (row[k] === undefined) delete row[k];
        });
        if (Object.keys(row).length > 0) next[index] = row;
      });
      return next;
    });
    commits.forEach(({ index, field, value }) => {
      setOrderStore(
        'order',
        'loads',
        loadIndex,
        'stops',
        index,
        produce((stop) => {
          stop.operationType =
            stop.operationType === 'None' ? 'Update' : stop.operationType;
          stop[field] = value;
        }),
      );
      const updates = createShowAuto214(index);
      setOrderStore(
        'order',
        'loads',
        loadIndex,
        'stops',
        index,
        produce((stop) => {
          stop.ediUpdates = updates;
        }),
      );
    });
  };

  const handleDriverTimeChange = (
    field: 'driverInTime' | 'driverOutTime',
    value: string | null,
    index: number,
    typedDate?: string,
    typedTime?: string,
  ) => {
    setIsDirty(true);
    const loadIndex = props.tabIndex;
    const activeIndices = getActiveStopIndices();
    const position = activeIndices.indexOf(index);
    const stop = orderStore.order.loads[loadIndex]?.stops?.[index];
    const partner =
      field === 'driverInTime' ? stop?.driverOutTime : stop?.driverInTime;
    const result = validateStopTimeChangeAtPositionFor(
      sidebarStops(),
      sidebarMode(),
      position,
      field,
      value,
      partner,
    );

    if (!result.ok) {
      if (field === 'driverInTime') {
        updateRowScratch(index, {
          driverInDate: typedDate,
          driverInTime: typedTime,
          driverInError: result.message,
        });
      } else {
        updateRowScratch(index, {
          driverOutDate: typedDate,
          driverOutTime: typedTime,
          driverOutError: result.message,
        });
      }
    } else if (field === 'driverInTime') {
      updateRowScratch(index, {
        driverInDate: undefined,
        driverInTime: undefined,
        driverInError: undefined,
      });
    } else {
      updateRowScratch(index, {
        driverOutDate: undefined,
        driverOutTime: undefined,
        driverOutError: undefined,
      });
    }

    if (!result.ok) {
      revalidateAllStops();
      return;
    }
    setOrderStore(
      'order',
      'loads',
      loadIndex,
      'stops',
      index,
      produce((stop) => {
        stop.operationType =
          stop.operationType === 'None' ? 'Update' : stop.operationType;
        stop[field] = value;
      }),
    );
    if (value) {
      const updates = createShowAuto214(index);
      setOrderStore(
        'order',
        'loads',
        loadIndex,
        'stops',
        index,
        produce((stop) => {
          stop.ediUpdates = updates;
        }),
      );
    }
    // A change to one stop can fix or break a sibling/own-partner error, so
    // re-derive the whole load's errors.
    revalidateAllStops();
  };

  const columns: Column<LastUsedTopStopModel>[] = [
    {
      name: 'Date',
      key: 'date',
      minWidth: 180,
      renderCell: (rowData) => (
        <Grid
          container
          gap={1}
          alignItems="center"
          style={{ 'flex-wrap': 'nowrap' }}
        >
          {rowData.pickUp && <ArrowUpCircleOutlineIcon style={iconStyles} />}
          {rowData.dropOff && <ArrowDownCircleOutlineIcon style={iconStyles} />}
          <Typography component="p" variant="inherit">
            {DateTime.fromISO(rowData.stopDateTime ?? '', {
              zone: 'utc',
            }).toFormat('MM-dd-yyyy')}
          </Typography>
        </Grid>
      ),
    },
    {
      name: 'Time',
      key: 'time',
      minWidth: isLtlMode() ? 120 : 320,
      renderCell: (rowData, index) => {
        const initial = rowScratch()[index] ?? {};
        const [timeIn, setTimeIn] = createSignal(
          initial.driverInTime ?? rowData.driverInTime?.split('T')[1] ?? '',
        );
        const [timeOut, setTimeOut] = createSignal(
          initial.driverOutTime ?? rowData.driverOutTime?.split('T')[1] ?? '',
        );
        const [dateIn, setDateIn] = createSignal(
          initial.driverInDate ?? rowData.driverInTime?.split('T')[0] ?? '',
        );
        const [dateOut, setDateOut] = createSignal(
          initial.driverOutDate ?? rowData.driverOutTime?.split('T')[0] ?? '',
        );
        const applyDriverIn = () => {
          const d = dateIn();
          const t = timeIn();
          handleDriverTimeChange(
            'driverInTime',
            d ? `${d}T${t || '00:00'}` : null,
            index,
            d,
            t,
          );
        };
        const applyDriverOut = () => {
          const d = dateOut();
          const t = timeOut();
          handleDriverTimeChange(
            'driverOutTime',
            d ? `${d}T${t || '00:00'}` : null,
            index,
            d,
            t,
          );
        };
        return (
          <div class="flex flex-col gap-1">
            <div class="text-sm">{rowData.time}</div>
            <Show
              when={
                !isLtlMode() &&
                isFeatureFlagEnabled(
                  FeatureFlags.rls_ui_show_stop_window_non_ltl,
                )()
              }
            >
              <div class="text-sm text-[rgba(0,0,0,0.87)]">Delivery Window</div>
              <div class="text-sm text-gray-500">Driver In</div>
              <div class="flex gap-2 mt-2 items-start [&_input]:min-w-0">
                <DatePicker
                  name="driverInTime"
                  label="Date"
                  class="[&>.MuiFormControl-root]:p-0!"
                  value={dateIn()}
                  handleChange={(value: string) => {
                    dirtyTracking(
                      rowData.pickUp ? 'pickUp' : 'dropOff',
                      'in',
                      index,
                    );
                    setDateIn(value);
                    applyDriverIn();
                  }}
                  disabled={
                    orderStore.isReadOnly || !canEditSidebarDriverIn(index)
                  }
                  dataTestId={`stop-date-${index}-datepicker`}
                />
                <Show
                  when={isFeatureFlagEnabled(
                    FeatureFlags.enable_24_hour_in_and_out_times,
                  )()}
                  fallback={
                    <TimePicker
                      name="driverInTime"
                      label="Time"
                      value={timeIn()}
                      disabled={
                        orderStore.isReadOnly || !canEditSidebarDriverIn(index)
                      }
                      handleChange={(value: string) => {
                        dirtyTracking(
                          rowData.pickUp ? 'pickUp' : 'dropOff',
                          'in',
                          index,
                        );
                        setTimeIn(value);
                        applyDriverIn();
                      }}
                    />
                  }
                >
                  <IntegerTimeInput
                    label="Time"
                    placeholder="HH:MM"
                    allowManualEntry
                    allowMidnight
                    floatLabel
                    variant="form"
                    height="h-[30px]"
                    class="w-[90px]"
                    value={timeStringToTimeInteger(timeIn())}
                    disabled={
                      orderStore.isReadOnly || !canEditSidebarDriverIn(index)
                    }
                    onChange={(value) => {
                      dirtyTracking(
                        rowData.pickUp ? 'pickUp' : 'dropOff',
                        'in',
                        index,
                      );
                      setTimeIn(timeIntegerToTimeString(value));
                      applyDriverIn();
                    }}
                  />
                </Show>
              </div>
              <Show when={rowScratch()[index]?.driverInError}>
                <div
                  class="text-xs text-red-600 mt-1"
                  data-testid={`stop-date-${index}-driverInError`}
                >
                  {rowScratch()[index]?.driverInError}
                </div>
              </Show>
              <div class="text-sm text-gray-500">Driver Out</div>
              <div class="flex gap-2 mt-2 items-start [&_input]:min-w-0">
                <DatePicker
                  name="driverOutTime"
                  label="Date"
                  class="[&>.MuiFormControl-root]:p-0!"
                  value={dateOut()}
                  handleChange={(value: string) => {
                    dirtyTracking(
                      rowData.pickUp ? 'pickUp' : 'dropOff',
                      'out',
                      index,
                    );
                    setDateOut(value);
                    applyDriverOut();
                  }}
                  disabled={
                    orderStore.isReadOnly || !canEditSidebarDriverOut(index)
                  }
                  dataTestId={`stop-date-${index}-datepicker`}
                />
                <Show
                  when={isFeatureFlagEnabled(
                    FeatureFlags.enable_24_hour_in_and_out_times,
                  )()}
                  fallback={
                    <TimePicker
                      name="driverOutTime"
                      label="Time"
                      value={timeOut()}
                      disabled={
                        orderStore.isReadOnly || !canEditSidebarDriverOut(index)
                      }
                      handleChange={(value: string) => {
                        dirtyTracking(
                          rowData.pickUp ? 'pickUp' : 'dropOff',
                          'out',
                          index,
                        );
                        setTimeOut(value);
                        applyDriverOut();
                      }}
                    />
                  }
                >
                  <IntegerTimeInput
                    label="Time"
                    placeholder="HH:MM"
                    allowManualEntry
                    allowMidnight
                    floatLabel
                    variant="form"
                    height="h-[30px]"
                    class="w-[90px]"
                    value={timeStringToTimeInteger(timeOut())}
                    disabled={
                      orderStore.isReadOnly || !canEditSidebarDriverOut(index)
                    }
                    onChange={(value) => {
                      dirtyTracking(
                        rowData.pickUp ? 'pickUp' : 'dropOff',
                        'out',
                        index,
                      );
                      setTimeOut(timeIntegerToTimeString(value));
                      applyDriverOut();
                    }}
                  />
                </Show>
              </div>
              <Show when={rowScratch()[index]?.driverOutError}>
                <div
                  class="text-xs text-red-600 mt-1"
                  data-testid={`stop-date-${index}-driverOutError`}
                >
                  {rowScratch()[index]?.driverOutError}
                </div>
              </Show>
              <div class="text-xs text-armstrong-blue">
                Time should be entered in the time zone of the stop location.
              </div>
            </Show>
          </div>
        );
      },
    },
    {
      name: 'Pick/Drop #',
      key: 'pickDrop',
      minWidth: 120,
      renderCell: (rowData) => {
        return <div class="text-xs">{rowData.referenceNum}</div>;
      },
    },
    {
      name: 'Address',
      key: 'location',
      minWidth: 200,
      renderCell: (rowData) => {
        return (
          <Stack spacing={1}>
            <Typography
              component="p"
              sxProps={{ fontWeight: 'bold' }}
              variant="inherit"
            >
              {rowData.locationName}
            </Typography>{' '}
            <Typography component="p" variant="inherit">
              {rowData.address1}, {rowData.address2}, {rowData.city},{' '}
              {rowData.state}, {rowData.zip}
            </Typography>
            <Typography component="p" variant="inherit">
              {rowData.contact}
              <br />
              {rowData.phone}
            </Typography>
          </Stack>
        );
      },
    },
    {
      name: 'Items',
      key: 'items',
      minWidth: 140,
      renderCell: (rowData) => {
        const renderItems = () => {
          const { items } = orderStore.order.loads[props.tabIndex];
          return items?.map((itm) => {
            if (
              itm.operationType !== 'Delete' &&
              (itm.pickUpId == rowData.id || itm.dropOffId == rowData.id)
            ) {
              return <p>{`${itm.quantity} ${itm.item}`}</p>;
            }
          });
        };

        return renderItems();
      },
    },
    {
      name: 'Stop Instructions',
      key: 'instructions',
      minWidth: 200,
      renderCell: (rowData) => (
        <PopoverCell id={rowData.id} value={rowData.instructions} />
      ),
    },
  ];

  return (
    <CollapsibleCard startTitle="Stops" loading={props.loading} class="mb-2">
      <div class="flex justify-end">
        <Show
          when={
            isFeatureFlagEnabled(
              FeatureFlags.rls_ui_show_stop_window_non_ltl,
            )() && !isLtlMode()
          }
        >
          <Button
            disabled={!isDirty()}
            onClick={async () => {
              const sidebarTabIndex = props.tabIndex;
              const stopsForSave = sidebarStops();
              if (!stopsForSave.length) return;
              // Block save if any inline (scratch) error is still showing.
              // Invalid intermediate values aren't persisted to the store, so
              // `collectStopChronologyViolations` can't see them — gate here.
              const scratchError = Object.values(rowScratch()).find(
                (s) => s?.driverInError || s?.driverOutError,
              );
              if (scratchError) {
                handleToast(
                  ToastType.Error,
                  scratchError.driverInError ??
                    scratchError.driverOutError ??
                    'Please fix the highlighted Driver In/Out errors.',
                );
                return;
              }
              // Block save if any chronology violation exists on this load.
              const violations = collectStopChronologyViolations().filter(
                (v) => v.loadIndex === sidebarTabIndex,
              );
              if (violations.length > 0) {
                handleToast(ToastType.Error, violations[0].message);
                return;
              }
              try {
                const response = (await apiClient.put(
                  '/stop/Update/DriverInOutTimes',
                  stopsForSave.map((stop) => ({
                    stopId: stop.id,
                    driverInTime: stop.driverInTime ?? '',
                    driverOutTime: stop.driverOutTime ?? '',
                  })),
                  buildChangeTrackingRequestConfig([
                    props.changeTrackingSource ??
                      CHANGE_TRACKING_SOURCES.orderDetails
                        .stopsUpdateDriverInOutTimes,
                  ]),
                )) as unknown as ApiResponse<string> | undefined;
                const newStatus = response?.value;
                if (newStatus) {
                  setOrderStore(
                    'order',
                    'loads',
                    sidebarTabIndex,
                    'status',
                    newStatus,
                  );
                  const loadId = orderStore.order.loads[sidebarTabIndex]?.id;
                  setLoadBoardStore(
                    'items',
                    (item) => item.loadId === loadId,
                    'status',
                    newStatus,
                  );
                }
                handleToast(ToastType.Success, 'Successfully saved changes');
                let allEdiUpdates: EdiUpdate[] = [];
                stopsForSave
                  .filter((stop) => stop.operationType !== 'Delete')
                  .forEach((stop) => {
                    allEdiUpdates = allEdiUpdates.concat(stop.ediUpdates ?? []);
                  });
                if (allEdiUpdates.length > 0) {
                  await Promise.all(
                    allEdiUpdates.map((update) =>
                      sendEDIUpdate(
                        update.loadId,
                        update as unknown as LoadEdiStatusUpdatePayload,
                      ),
                    ),
                  );
                  handleToast(
                    ToastType.Success,
                    'EDI Updates sent successfully',
                  );
                }
                setOrderStore(
                  'order',
                  'loads',
                  sidebarTabIndex,
                  'stops',
                  () => true,
                  'shouldSendEdiBooleanVals',
                  {
                    shouldSendPickupAppointmentAuto214: false,
                    shouldSendDropoffAppointmentAuto214: false,
                    shouldSendDropoffDriverInAuto214: false,
                    shouldSendPickupDriverInAuto214: false,
                    shouldSendPickupDriverOutAuto214: false,
                    shouldSendDropoffDriverOutAuto214: false,
                  },
                );
                setOrderStore(
                  'order',
                  'loads',
                  sidebarTabIndex,
                  'stops',
                  () => true,
                  'ediUpdates',
                  [],
                );
                setIsDirty(false);
              } catch (error: unknown) {
                handleError(error);
              }
            }}
            startIcon={<ContentSaveIcon size={20} />}
            label="Save Changes"
            dataTestId="save-changes-button"
          />
        </Show>
      </div>
      <BasicTable<LastUsedTopStopModel>
        columns={columns}
        rows={stops()}
        footerBorder={false}
      />
    </CollapsibleCard>
  );
};
